import { useEffect, useState, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, ArrowLeft, BarChart3, CheckCircle2, AlertCircle } from 'lucide-react';
import Navigation from '@/components/Navigation';
import SEO from '@/components/SEO';
import Breadcrumbs from '@/components/Breadcrumbs';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { EarlyAccessForm } from '@/components/EarlyAccessForm';
import { FOUNDER, founderPersonLd } from '@/lib/founder';

type Dimension = 'capture' | 'coding' | 'workflow' | 'exceptions' | 'supplier' | 'audit';

interface Question {
  id: string;
  dimension: Dimension;
  text: string;
  options: { label: string; score: number }[];
}

const DIMENSIONS: Record<Dimension, { label: string; description: string }> = {
  capture: {
    label: 'Capture',
    description: 'How invoices enter the AP process and how accurately data extracts',
  },
  coding: {
    label: 'Coding',
    description: 'GL coding accuracy and automation level on PO and non-PO invoices',
  },
  workflow: {
    label: 'Workflow',
    description: 'Approval routing, cycle time, and straight-through processing rate',
  },
  exceptions: {
    label: 'Exceptions',
    description: 'How exceptions are identified, routed, and resolved',
  },
  supplier: {
    label: 'Supplier engagement',
    description: 'Supplier email triage, portal adoption, payment-status inquiries',
  },
  audit: {
    label: 'Audit & compliance',
    description: 'Audit trail completeness, compliance reporting, controls',
  },
};

const QUESTIONS: Question[] = [
  {
    id: 'c1',
    dimension: 'capture',
    text: 'What percentage of invoices arrive electronically (PDF, EDI, structured) vs. paper/scanned?',
    options: [
      { label: 'Mostly paper / scanned (>50%)', score: 1 },
      { label: 'Mixed — 30-50% electronic', score: 2 },
      { label: 'Majority electronic — 50-80%', score: 3 },
      { label: 'Mostly electronic — 80-95%', score: 4 },
      { label: 'Fully electronic — 95%+', score: 5 },
    ],
  },
  {
    id: 'c2',
    dimension: 'capture',
    text: 'What is your day-one data extraction accuracy on a new supplier (header + line items)?',
    options: [
      { label: 'Below 60% — significant manual keying required', score: 1 },
      { label: '60-75% — verifier queue handles the gap', score: 2 },
      { label: '75-85% — moderate verifier work', score: 3 },
      { label: '85-95% — most fields extract cleanly', score: 4 },
      { label: '95%+ — verifier review is rare', score: 5 },
    ],
  },
  {
    id: 'co1',
    dimension: 'coding',
    text: 'What percentage of non-PO invoices auto-code without human intervention?',
    options: [
      { label: '0-20% — most coded manually', score: 1 },
      { label: '20-40%', score: 2 },
      { label: '40-60%', score: 3 },
      { label: '60-80%', score: 4 },
      { label: '80%+ — coding is mostly automated', score: 5 },
    ],
  },
  {
    id: 'co2',
    dimension: 'coding',
    text: 'For PO-based invoices, what is your three-way match rate before exceptions?',
    options: [
      { label: 'Below 50%', score: 1 },
      { label: '50-65%', score: 2 },
      { label: '65-80%', score: 3 },
      { label: '80-90%', score: 4 },
      { label: '90%+ — most invoices match cleanly', score: 5 },
    ],
  },
  {
    id: 'w1',
    dimension: 'workflow',
    text: 'Average approval cycle time from invoice receipt to ready-to-pay?',
    options: [
      { label: 'More than 14 days', score: 1 },
      { label: '8-14 days', score: 2 },
      { label: '4-7 days', score: 3 },
      { label: '2-3 days', score: 4 },
      { label: 'Less than 2 days', score: 5 },
    ],
  },
  {
    id: 'w2',
    dimension: 'workflow',
    text: 'Can approvers process invoices on mobile devices without an ERP license?',
    options: [
      { label: 'No — desktop only, ERP login required', score: 1 },
      { label: 'Limited — basic mobile but feature-poor', score: 2 },
      { label: 'Functional mobile, ERP license still required', score: 3 },
      { label: 'Mobile-ready, no ERP license required', score: 4 },
      { label: 'Full mobile + email-based approval, no ERP license', score: 5 },
    ],
  },
  {
    id: 'e1',
    dimension: 'exceptions',
    text: 'What percentage of your AP team\'s time is spent on exception resolution vs. routine processing?',
    options: [
      { label: '60%+ on exceptions — exceptions dominate workload', score: 1 },
      { label: '40-60% on exceptions', score: 2 },
      { label: '25-40% on exceptions', score: 3 },
      { label: '15-25% on exceptions', score: 4 },
      { label: 'Less than 15% — exception handling is the minority of the work', score: 5 },
    ],
  },
  {
    id: 'e2',
    dimension: 'exceptions',
    text: 'Are exception types categorized and routed automatically to specialized handlers?',
    options: [
      { label: 'No — all exceptions go to one queue', score: 1 },
      { label: 'Loosely categorized but mostly manual routing', score: 2 },
      { label: 'Categorized; some auto-routing', score: 3 },
      { label: 'Auto-categorized and auto-routed to specialists', score: 4 },
      { label: 'Auto-routed AND most categories auto-resolve before reaching a human', score: 5 },
    ],
  },
  {
    id: 's1',
    dimension: 'supplier',
    text: 'How much AP team time is spent on supplier email triage (status inquiries, missing PO numbers, etc.)?',
    options: [
      { label: 'Heavy — 2+ FTE-equivalent', score: 1 },
      { label: 'Significant — 1-2 FTE-equivalent', score: 2 },
      { label: 'Moderate — half an FTE', score: 3 },
      { label: 'Limited — under 25% of one FTE', score: 4 },
      { label: 'Negligible — supplier email is automated upstream', score: 5 },
    ],
  },
  {
    id: 's2',
    dimension: 'supplier',
    text: 'Do suppliers have a self-service portal for invoice status, payment status, and document submission?',
    options: [
      { label: 'No portal — suppliers email or call for everything', score: 1 },
      { label: 'Basic portal — limited adoption (<20% of suppliers)', score: 2 },
      { label: 'Functional portal — 20-50% supplier adoption', score: 3 },
      { label: 'Strong portal — 50-80% supplier adoption', score: 4 },
      { label: 'Mature portal + AI-assisted email automation — 80%+ adoption', score: 5 },
    ],
  },
  {
    id: 'a1',
    dimension: 'audit',
    text: 'How easily can you produce a complete approval-and-payment audit trail for a single invoice?',
    options: [
      { label: 'Requires manual data gathering across multiple systems', score: 1 },
      { label: 'Possible but slow — needs IT or report-running', score: 2 },
      { label: 'Reasonable — AP staff can produce within a day', score: 3 },
      { label: 'Fast — self-service export within minutes', score: 4 },
      { label: 'Instant — single click produces a complete, structured trail', score: 5 },
    ],
  },
  {
    id: 'a2',
    dimension: 'audit',
    text: 'For regulated/restricted funds (grants, Title funds, restricted donations, etc.), how is funding-source compliance tracked?',
    options: [
      { label: 'Manual — spreadsheets reconciled periodically', score: 1 },
      { label: 'Partial system tracking + manual reconciliation', score: 2 },
      { label: 'Tracked in the ERP but with manual coding effort', score: 3 },
      { label: 'Tracked at the invoice line and enforced through approval rules', score: 4 },
      { label: 'Auto-tracked, auto-enforced, audit-exportable instantly', score: 5 },
    ],
  },
];

interface DimensionScore {
  dimension: Dimension;
  raw: number;
  max: number;
  pct: number;
  level: string;
  color: string;
}

function levelFromPct(pct: number): { label: string; color: string } {
  if (pct >= 80) return { label: 'Mature', color: 'bg-green-500' };
  if (pct >= 60) return { label: 'Capable', color: 'bg-blue-500' };
  if (pct >= 40) return { label: 'Developing', color: 'bg-yellow-500' };
  if (pct >= 20) return { label: 'Emerging', color: 'bg-orange-500' };
  return { label: 'Manual', color: 'bg-red-500' };
}

const ApMaturityBenchmark = () => {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const [step, setStep] = useState(0); // 0 = intro, 1..N = questions, N+1 = results
  const [answers, setAnswers] = useState<Record<string, number>>({});

  const scores = useMemo((): DimensionScore[] => {
    const byDim = Object.keys(DIMENSIONS).map((d) => {
      const qs = QUESTIONS.filter((q) => q.dimension === d);
      const raw = qs.reduce((sum, q) => sum + (answers[q.id] ?? 0), 0);
      const max = qs.length * 5;
      const pct = max > 0 ? (raw / max) * 100 : 0;
      const { label, color } = levelFromPct(pct);
      return { dimension: d as Dimension, raw, max, pct, level: label, color };
    });
    return byDim;
  }, [answers]);

  const overallPct = useMemo(() => {
    const totalRaw = scores.reduce((s, d) => s + d.raw, 0);
    const totalMax = scores.reduce((s, d) => s + d.max, 0);
    return totalMax > 0 ? (totalRaw / totalMax) * 100 : 0;
  }, [scores]);

  const overallLevel = levelFromPct(overallPct);

  const personSchema = { '@context': 'https://schema.org', ...founderPersonLd };

  const totalQuestions = QUESTIONS.length;
  const onIntro = step === 0;
  const onResults = step > totalQuestions;
  const onQuestion = step >= 1 && step <= totalQuestions;
  const currentQuestion = onQuestion ? QUESTIONS[step - 1] : null;
  const answeredCount = Object.keys(answers).length;
  const allAnswered = answeredCount === totalQuestions;

  return (
    <div className="min-h-screen bg-white">
      <SEO
        title="AP Automation Maturity Benchmark | EZ Cloud"
        description="Free interactive self-assessment for AP teams. Score your AP automation maturity across six dimensions — capture, coding, workflow, exceptions, supplier engagement, audit & compliance. Benchmark-style report with recommendations."
        canonical="https://ezcloud.ai/resources/ap-maturity-benchmark"
        breadcrumbs={[
          { name: 'Home', url: 'https://ezcloud.ai/' },
          { name: 'Resources', url: 'https://ezcloud.ai/resources/ap-maturity-benchmark' },
          {
            name: 'AP Maturity Benchmark',
            url: 'https://ezcloud.ai/resources/ap-maturity-benchmark',
          },
        ]}
      />
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(personSchema) }}
      />
      <Navigation />
      <Breadcrumbs />

      <main>
        <section className="bg-hero-gradient text-white pt-16 pb-16 relative overflow-hidden">
          <div className="absolute inset-0 pointer-events-none">
            <div className="absolute top-0 left-0 w-1/2 h-full bg-gradient-to-br from-primary/20 to-transparent" />
            <div className="absolute top-20 right-20 w-96 h-96 bg-accent/10 rounded-full blur-3xl" />
          </div>
          <div className="container mx-auto px-4 max-w-4xl relative">
            <div className="flex items-center gap-2 text-accent text-sm font-semibold uppercase tracking-wide mb-4">
              <BarChart3 className="w-4 h-4" aria-hidden />
              Free Tool · Self-Assessment
            </div>
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold leading-tight mb-6">
              AP Automation Maturity Benchmark
            </h1>
            <p className="text-xl md:text-2xl text-white/80 max-w-3xl">
              Score your AP automation maturity across six dimensions. Twelve questions, three
              minutes, a maturity report you can share internally.
            </p>
          </div>
        </section>

        {/* Intro */}
        {onIntro && (
          <section className="py-16 bg-white">
            <div className="container mx-auto px-4 max-w-3xl">
              <Card className="p-8 bg-white border border-gray-200 shadow-md">
                <h2 className="text-2xl md:text-3xl font-bold text-gray-900 mb-4">
                  Before you start
                </h2>
                <p className="text-gray-700 leading-relaxed mb-5">
                  The benchmark scores AP automation across six dimensions: capture, coding,
                  workflow, exceptions, supplier engagement, and audit. Each dimension carries
                  equal weight in the overall score.
                </p>
                <p className="text-gray-700 leading-relaxed mb-5">
                  Answer honestly. The output is a maturity report scoped to your real situation.
                  No data is sent anywhere — the assessment runs entirely in your browser. You can
                  optionally request a printable PDF report at the end.
                </p>
                <div className="grid sm:grid-cols-2 gap-3 mt-6 mb-8">
                  {Object.entries(DIMENSIONS).map(([k, v]) => (
                    <div
                      key={k}
                      className="flex items-start gap-3 bg-gray-50 border border-gray-200 rounded-lg p-3"
                    >
                      <CheckCircle2 className="w-5 h-5 text-accent flex-shrink-0 mt-0.5" aria-hidden />
                      <div>
                        <p className="text-sm font-semibold text-gray-900">{v.label}</p>
                        <p className="text-xs text-gray-600 mt-0.5">{v.description}</p>
                      </div>
                    </div>
                  ))}
                </div>
                <Button
                  size="lg"
                  variant="accent"
                  className="font-semibold"
                  onClick={() => setStep(1)}
                >
                  Start the benchmark
                  <ArrowRight className="ml-2 w-5 h-5" aria-hidden />
                </Button>
              </Card>
            </div>
          </section>
        )}

        {/* Questions */}
        {onQuestion && currentQuestion && (
          <section className="py-16 bg-white">
            <div className="container mx-auto px-4 max-w-3xl">
              <div className="mb-6 flex items-center justify-between">
                <p className="text-sm font-semibold text-gray-600">
                  Question {step} of {totalQuestions}
                </p>
                <p className="text-xs uppercase tracking-wide font-bold text-accent">
                  {DIMENSIONS[currentQuestion.dimension].label}
                </p>
              </div>
              <div className="w-full bg-gray-200 rounded-full h-1.5 mb-8">
                <div
                  className="bg-accent h-1.5 rounded-full transition-all"
                  style={{ width: `${(step / totalQuestions) * 100}%` }}
                />
              </div>
              <Card className="p-7 bg-white border border-gray-200 shadow-md">
                <h2 className="text-xl md:text-2xl font-bold text-gray-900 mb-6 leading-snug">
                  {currentQuestion.text}
                </h2>
                <div className="space-y-3">
                  {currentQuestion.options.map((opt) => {
                    const isSelected = answers[currentQuestion.id] === opt.score;
                    return (
                      <button
                        key={opt.label}
                        onClick={() => {
                          setAnswers({ ...answers, [currentQuestion.id]: opt.score });
                          setTimeout(() => setStep(step + 1), 200);
                        }}
                        className={`w-full text-left p-4 border rounded-lg transition-all ${
                          isSelected
                            ? 'border-accent bg-accent/10'
                            : 'border-gray-200 hover:border-accent hover:bg-accent/5'
                        }`}
                      >
                        <p className="text-gray-900">{opt.label}</p>
                      </button>
                    );
                  })}
                </div>
                <div className="mt-6 flex justify-between">
                  <Button
                    variant="outline"
                    onClick={() => setStep(Math.max(0, step - 1))}
                    disabled={step === 0}
                  >
                    <ArrowLeft className="mr-2 w-4 h-4" aria-hidden />
                    Back
                  </Button>
                  {step === totalQuestions && allAnswered && (
                    <Button
                      variant="accent"
                      onClick={() => setStep(totalQuestions + 1)}
                    >
                      See my results
                      <ArrowRight className="ml-2 w-4 h-4" aria-hidden />
                    </Button>
                  )}
                </div>
              </Card>
            </div>
          </section>
        )}

        {/* Results */}
        {onResults && (
          <>
            <section className="py-12 bg-gray-50 border-b border-gray-200">
              <div className="container mx-auto px-4 max-w-4xl">
                <p className="text-sm font-semibold uppercase tracking-wide text-accent mb-3 text-center">
                  Your AP automation maturity
                </p>
                <h2 className="text-3xl md:text-5xl font-bold text-gray-900 text-center mb-3">
                  Overall:{' '}
                  <span className="text-accent">
                    {overallLevel.label} ({Math.round(overallPct)}%)
                  </span>
                </h2>
                <p className="text-center text-gray-600 max-w-2xl mx-auto">
                  This is your composite score across the six dimensions. The breakdown below
                  shows where you're strongest and where there's most room.
                </p>
              </div>
            </section>

            <section className="py-12 bg-white">
              <div className="container mx-auto px-4 max-w-4xl">
                <div className="space-y-5">
                  {scores
                    .sort((a, b) => b.pct - a.pct)
                    .map((s) => (
                      <Card
                        key={s.dimension}
                        className="p-6 bg-white border border-gray-200 shadow-sm"
                      >
                        <div className="flex items-start justify-between mb-3 gap-3 flex-wrap">
                          <div>
                            <h3 className="text-xl font-bold text-gray-900">
                              {DIMENSIONS[s.dimension].label}
                            </h3>
                            <p className="text-sm text-gray-600 mt-1">
                              {DIMENSIONS[s.dimension].description}
                            </p>
                          </div>
                          <div className="text-right">
                            <p className="text-2xl font-bold text-gray-900">{Math.round(s.pct)}%</p>
                            <p className="text-sm text-gray-600">{s.level}</p>
                          </div>
                        </div>
                        <div className="w-full bg-gray-100 rounded-full h-2 mt-3">
                          <div
                            className={`${s.color} h-2 rounded-full transition-all`}
                            style={{ width: `${s.pct}%` }}
                          />
                        </div>
                      </Card>
                    ))}
                </div>
                <div className="mt-8 p-5 bg-gray-50 border border-gray-200 rounded-lg flex items-start gap-3">
                  <AlertCircle className="w-5 h-5 text-accent flex-shrink-0 mt-0.5" aria-hidden />
                  <p className="text-sm text-gray-600">
                    Maturity levels:{' '}
                    <strong className="text-gray-900">Mature</strong> (80%+),{' '}
                    <strong className="text-gray-900">Capable</strong> (60-79%),{' '}
                    <strong className="text-gray-900">Developing</strong> (40-59%),{' '}
                    <strong className="text-gray-900">Emerging</strong> (20-39%),{' '}
                    <strong className="text-gray-900">Manual</strong> (under 20%).
                  </p>
                </div>
              </div>
            </section>

            <section className="py-12 bg-gray-50 border-t border-gray-200">
              <div className="container mx-auto px-4 max-w-3xl">
                <p className="text-sm font-semibold uppercase tracking-wide text-accent mb-3 text-center">
                  Save your results
                </p>
                <h2 className="text-3xl font-bold text-gray-900 text-center mb-4 leading-tight">
                  Get a printable PDF of your maturity report
                </h2>
                <p className="text-center text-gray-600 mb-8 max-w-2xl mx-auto">
                  We'll email a printable PDF including your dimensional scores, the overall
                  maturity level, and tailored recommendations for advancing each weak area.
                </p>
                <EarlyAccessForm
                  tool="AP Maturity Benchmark"
                  source="maturity-benchmark"
                  eventName="ap_maturity_benchmark_request"
                  buttonLabel="Email me the printable report"
                />
              </div>
            </section>

            <section className="py-16 bg-hero-gradient text-white">
              <div className="container mx-auto px-4 max-w-3xl text-center">
                <h2 className="text-3xl md:text-4xl font-bold mb-4">
                  Want to talk through your scores?
                </h2>
                <p className="text-xl text-white/80 mb-8 leading-relaxed">
                  A 30-minute call with {FOUNDER.name} to discuss what each dimension actually
                  looks like at higher maturity — and what it would take for your AP operation to
                  get there.
                </p>
                <Link
                  to="/solutions/modernize-oracle-webcenter"
                  className="inline-flex items-center font-semibold text-white bg-accent hover:bg-accent/90 transition-colors px-6 py-3 rounded-lg"
                >
                  See the WebCenter modernization path
                  <ArrowRight className="ml-2 w-5 h-5" aria-hidden />
                </Link>
              </div>
            </section>
          </>
        )}
      </main>
    </div>
  );
};

export default ApMaturityBenchmark;
